/**
 * Product Builder mode — reusable detection for demo / product-builder tenants.
 *
 * Resolution order (first match wins):
 * 1. Entitlements: objectManager.product_builder or objectManager.productBuilder
 * 2. Cached session flag (from prior feature-flag API call this session)
 * 3. public.system_feature_flags row: feature_key = product_builder (via GET /api/features/flags/:key)
 */
import { useEffect, useState } from 'react';
import { getEntitlementsSection } from './entitlements';
import { isFeatureEnabled } from '../api/FeatureFlagApi';
import { PRODUCT_BUILDER_FEATURE_KEY } from './standardObjectPublishing';

const SESSION_CACHE_KEY = 'spark_product_builder_mode';

const isTruthyFlag = (value) =>
  value === true || value === 'true' || value === 1 || value === '1';

export function getProductBuilderModeFromEntitlements() {
  const objectManagerConfig = getEntitlementsSection('objectManager', {});
  return (
    isTruthyFlag(objectManagerConfig?.product_builder)
    || isTruthyFlag(objectManagerConfig?.productBuilder)
  );
}

export function readCachedProductBuilderMode() {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
  if (raw === '1') return true;
  if (raw === '0') return false;
  return null;
}

export function cacheProductBuilderMode(enabled) {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(SESSION_CACHE_KEY, enabled ? '1' : '0');
}

/**
 * Resolve Product Builder mode asynchronously (entitlements → cache → feature flag API).
 * @returns {Promise<boolean>}
 */
export async function resolveProductBuilderMode() {
  if (getProductBuilderModeFromEntitlements()) {
    cacheProductBuilderMode(true);
    return true;
  }

  const cached = readCachedProductBuilderMode();
  if (cached !== null) {
    return cached;
  }

  const enabled = await isFeatureEnabled(PRODUCT_BUILDER_FEATURE_KEY);
  cacheProductBuilderMode(enabled);
  return enabled;
}

/**
 * React hook: resolves Product Builder mode once on mount (and when entitlements update).
 */
export function useProductBuilderMode() {
  const [productBuilderMode, setProductBuilderMode] = useState(() => {
    if (getProductBuilderModeFromEntitlements()) return true;
    const cached = readCachedProductBuilderMode();
    return cached === true;
  });

  useEffect(() => {
    let cancelled = false;

    if (getProductBuilderModeFromEntitlements()) {
      setProductBuilderMode(true);
      cacheProductBuilderMode(true);
      return undefined;
    }

    const cached = readCachedProductBuilderMode();
    if (cached !== null) {
      setProductBuilderMode(cached);
      return undefined;
    }

    resolveProductBuilderMode().then((enabled) => {
      if (!cancelled) setProductBuilderMode(enabled);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return productBuilderMode;
}
